/**
 * 統合NFCサービス
 * プラットフォームに応じて適切なNFCサービスを選択
 */

import { INFCService, NFCEventHandler } from './nfcServiceInterface';
import { WebSocketNFCService } from './websocketNFCService';
import { WebNFCService } from './webNFCService';
import { platformDetector } from '../platformDetector';
import { showNotification } from '@mantine/notifications';

class UnifiedNFCService implements INFCService {
  private service: INFCService | null = null;
  private platform = platformDetector.getPlatformInfo();
  private pendingHandlers: Array<{
    event: string;
    handler: NFCEventHandler;
    unsubscribe?: () => void;
  }> = [];

  constructor() {}

  /**
   * プラットフォームに応じた適切なサービスを初期化
   */
  private initializeService(): INFCService {
    const { nfcCapability } = this.platform;

    switch (nfcCapability) {
      case 'web-nfc':
        return new WebNFCService();

      case 'websocket':
        return new WebSocketNFCService();

      default:
        throw new Error(`NFC is not supported on this platform: ${this.platform.platform}`);
    }
  }

  async connect(): Promise<void> {
    // 既に接続されている場合はスキップ
    if (this.service && this.service.isConnected()) {
      return;
    }

    // プラットフォームのNFC対応をチェック
    if (!platformDetector.isNFCAvailable()) {
      const limitations = platformDetector.getPlatformLimitations();
      const message = limitations.join('\n');

      // NFCが利用できない環境ではエラーを投げずに早期リターン（非ブロッキング）
      return;
    }

    try {
      // サービスを初期化
      if (!this.service) {
        this.service = this.initializeService();
      }

      // 接続
      await this.service.connect();

      // サービス初期化後に保留中のハンドラーを登録
      this.registerPendingHandlers();
    } catch (error: any) {
      // Android特有のエラーハンドリング
      if (this.platform.isAndroid) {
        if (error.name === 'NotAllowedError') {
          showNotification({
            title: 'NFC権限が必要です',
            message: 'ブラウザの設定でNFC権限を許可してください',
            color: 'orange',
            autoClose: false,
          });
        } else if (!this.platform.hasWebNFC) {
          showNotification({
            title: 'ブラウザ未対応',
            message: 'Chrome または Edge を使用してください',
            color: 'orange',
            autoClose: false,
          });
        } else if (!window.isSecureContext) {
          showNotification({
            title: 'HTTPS接続が必要',
            message: 'セキュアな接続（HTTPS）でアクセスしてください',
            color: 'orange',
            autoClose: false,
          });
        }
      }

      throw error;
    }
  }

  disconnect(): void {
    if (this.service) {
      this.service.disconnect();
    }
  }

  isConnected(): boolean {
    return this.service?.isConnected() || false;
  }

  async requestWriteCard(type: number, entityId: number): Promise<void> {
    if (!this.service) {
      throw new Error('NFC service is not initialized');
    }

    if (!this.service.isConnected()) {
      throw new Error('NFC service is not connected');
    }

    return this.service.requestWriteCard(type, entityId);
  }

  isWriteMode(): boolean {
    return this.service?.isWriteMode() || false;
  }

  getDeviceId(): string {
    return this.service?.getDeviceId() || '';
  }

  getServiceType(): 'websocket' | 'web-nfc' | 'mock' {
    return this.service?.getServiceType() || 'mock';
  }

  /**
   * 保留中のハンドラーをサービス初期化後に登録
   */
  private registerPendingHandlers(): void {
    if (!this.service) {
      return;
    }

    this.pendingHandlers.forEach((pendingHandler) => {
      try {
        const unsubscribe = this.service!.on(pendingHandler.event, pendingHandler.handler);
        pendingHandler.unsubscribe = unsubscribe;
      } catch (_error) {
        // Failed to register pending handler
      }
    });
  }

  on(event: string, handler: NFCEventHandler): () => void {
    if (!this.service) {
      // ハンドラーを保留リストに追加
      const pendingHandler: { event: string; handler: NFCEventHandler; unsubscribe?: () => void } =
        { event, handler };
      this.pendingHandlers.push(pendingHandler);

      // アンサブスクライブ関数を返す
      return () => {
        // 保留リストから削除
        const index = this.pendingHandlers.indexOf(pendingHandler);
        if (index > -1) {
          this.pendingHandlers.splice(index, 1);
        }

        // 既に登録済みの場合はアンサブスクライブ
        if (pendingHandler.unsubscribe) {
          pendingHandler.unsubscribe();
        }
      };
    }

    return this.service.on(event, handler);
  }

  off(event: string): void {
    this.service?.off(event);
  }

  clear(): void {
    this.service?.clear();
    // 保留中のハンドラーもクリア
    this.pendingHandlers = [];
  }

  /**
   * 現在のプラットフォーム情報を取得
   */
  getPlatformInfo() {
    return this.platform;
  }

  /**
   * NFCサービスの状態を取得（デバッグ用）
   */
  getStatus() {
    return {
      platform: this.platform.platform,
      nfcCapability: this.platform.nfcCapability,
      serviceType: this.getServiceType(),
      isConnected: this.isConnected(),
      isWriteMode: this.isWriteMode(),
      deviceId: this.getDeviceId(),
      pendingHandlers: this.pendingHandlers.length,
    };
  }

  /**
   * 手動でNFCスキャンを開始（主にAndroid用）
   */
  async startManualScan(): Promise<void> {
    if (!this.service) {
      throw new Error('NFC service is not initialized');
    }

    // Web NFCサービスの場合のみ手動スキャンが必要
    if (this.service.startManualScan) {
      return this.service.startManualScan();
    }

    // WebSocketサービスは常時スキャン中
  }
}

// シングルトンインスタンス
export const unifiedNFCService = new UnifiedNFCService();
